import type { RecordFileItem } from '@ofmos/shared/files';
import type { FilesClient } from '../client';
import type { Transport } from './transport';

/**
 * `FilesClient` over `/api/files/*` (`docs/decisions/file-attachments.md` D3, D5).
 *
 * THIN, like the CSV client, and here the thinness is the design rather than a convention.
 * Every rule this module has - the size cap, the empty-file refusal, what the bytes actually
 * ARE, whether this principal may attach them, whether the record may be read - is decided
 * on the server, on the bytes, at the moment of the request. A second opinion in the browser
 * could only ever be a stale one; the client pre-checks that DO exist (size, remaining count)
 * are in `FilesInput`, where they save a round trip rather than making a decision.
 *
 * TWO ROUTES ARE NOT JSON, and both go through the ONE transport (obligation 50). The upload
 * PUTs a raw `application/octet-stream` body, which is why `rawBody.body` widened to accept a
 * `Blob`; the download asks for a Blob back rather than text, because `response.text()` on
 * 25 MB of PDF produces a string of replacement characters that looks like a success.
 */
export class HttpFilesClient implements FilesClient {
  constructor(private readonly transport: Transport) {}

  reserve(input: {
    readonly label: string;
    readonly byteCount: number;
  }): Promise<{ readonly fileId: string; readonly expiresAt: string | null }> {
    return this.transport.request<{ fileId: string; expiresAt: string | null }>({
      method: 'POST',
      path: '/api/files',
      body: { label: input.label, byteCount: input.byteCount },
    });
  }

  upload(fileId: string, bytes: Blob): Promise<RecordFileItem> {
    return this.transport.request<RecordFileItem>({
      method: 'PUT',
      path: `/api/files/${fileId}/content`,
      /**
       * The declared type is `application/octet-stream` WHATEVER the browser thinks the file
       * is, and that is D4 stated from this side: the server derives the real type from the
       * bytes, so sending the browser's guess would offer it something it must not read.
       */
      rawBody: { contentType: 'application/octet-stream', body: bytes },
    });
  }

  download(fileId: string): Promise<Blob> {
    return this.transport.requestBlob({
      method: 'GET',
      path: `/api/files/${fileId}/content`,
    });
  }

  /**
   * D5: same-origin, so the session cookie rides along on a subresource request and the
   * permission check a signed URL exists to replace is already on it. No token, no expiry,
   * nothing that could land in browser history as a bearer capability.
   */
  previewUrl(fileId: string): string {
    return `/api/files/${fileId}/content`;
  }
}
